import { Box, Chip, IconButton, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import Iconify from '../../../../reusable-components/iconify';
import { sendTicketMessage } from '../../../../redux/slices/tickets';
import { useDispatch, useSelector } from '../../../../redux/store';
import Editor from '../../../../reusable-components/editor';
import { useSettingsCheck } from '../../../../reusable-components/settings';
import { useTabs } from '../../../../context/TabContext';
import { ticketMessageSubmitHtmlFormatter } from './newTicket/ticket-message-input-cleanup';

// ----------------------------------------------------------------------

export default function TicketMessageInput() {
  const location = useLocation();
  const { id = '' } = useParams();
  const subticketId = new URLSearchParams(location.search).get('subticket');

  const { users } = useSelector((state) => state.tickets.data.ticketInfoById[id || subticketId]) || [];
  const { mainTicket, subtickets } = useSelector((state) => state.tickets.data.ticketInfoById[id]) || {};
  const currentTicketData = subticketId ? subtickets?.find((st) => st.id === parseInt(subticketId, 10)) : mainTicket;
  const rawSuggestedResponses = currentTicketData?.suggestedResponses;
  const suggestedResponses = [...(rawSuggestedResponses || [])].sort((a, b) => (a.text?.length || 0) - (b.text?.length || 0));
  const [suggestedResponseUsed, setSuggestedResponseUsed] = useState(false);
  const [prependText, setPrependText] = useState(null);
  useEffect(() => {
    setSuggestedResponseUsed(false);
  }, [rawSuggestedResponses]);

  const signature = useSettingsCheck('AutoSignatureOnTickets');
  const defaultMessage = signature?.show ? signature?.value : '';

  const { ticketNumber } = useSelector((state) => state.tickets.data.ticketById[subticketId || id]) || {};
  const { user } = useSelector((state) => state.user);
  const { activeTab } = useTabs();

  const baseUrl = window.location.href.includes('&start')
    ? window.location.href.split('&start')[0]
    : window.location.href;
  const sessionStorageKey = `tab-${activeTab.key}-editor-${baseUrl}`;

  const loadMessageFromSession = () => {
    const savedMessage = sessionStorage.getItem(sessionStorageKey);
    return savedMessage ? JSON.parse(savedMessage) : defaultMessage;
  };

  const clearMessageFromSession = () => {
    sessionStorage.removeItem(sessionStorageKey);
  };

  const dispatch = useDispatch();

  const [ticketEmailsOn, setTicketEmailsOn] = useState(true);
  const [message, setMessage] = useState(loadMessageFromSession);
  useEffect(() => {
    setMessage(loadMessageFromSession);
  }, [location]);

  const [attachments, setAttachments] = useState([]);

  const handleFileInputChange = (event) => {
    const maxAllowedFiles = 5;
    if (
      event.target.files.length > maxAllowedFiles ||
      event.target.files.length + attachments?.length > maxAllowedFiles
    ) {
      alert(`Only ${maxAllowedFiles} files can be uploaded at a time.`);
      event.target.value = '';
      return;
    }
    const { files } = event.target;
    const fileList = [];
    for (let i = 0; i < files.length; i++) {
      if (attachments?.some((x) => x.name === files[i].name)) {
        alert(`File ${files[i].name} already exists.`);
      } else fileList.push(files[i]);
    }
    setAttachments((prevAttachments) => {
      if (prevAttachments === undefined) {
        return fileList;
      }
      return [...prevAttachments, ...fileList];
    });
  };
  const removeFile = (file) => () => {
    setAttachments((prevAttachments) => prevAttachments.filter((x) => x !== file));
  };

  const listOfUsers = async (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const mentionElements = doc.querySelectorAll('span.mention');
    const users = Array.from(mentionElements).map((mention) => mention.getAttribute('data-id'));
    return users;
  };

  const handleSend = async () => {
    if (!message && attachments?.length === 0) return;
    // if ((!message && attachments?.length === 0) || message === defaultMessage) return;
    
    setMessage(defaultMessage);
    setAttachments([]);
    clearMessageFromSession();

    const formattedHtml = ticketMessageSubmitHtmlFormatter(message);
    const usersList = await listOfUsers(message);
    usersList.push(user.email);
    try {
      await dispatch(
        sendTicketMessage({
          message: formattedHtml,
          ticketNumber: subticketId || ticketNumber || id,
          files: attachments,
          users: usersList,
          notify: ticketEmailsOn,
          sentBy: user.email,
          fullName: `${user.firstName} ${user.lastName}`,
        })
      );
    } catch (error) {
      console.error(error);
    }   
  };

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const draftMessage = query.get('draft');
    if (draftMessage) {
      setMessage(decodeURIComponent(draftMessage));
    }
  }, [location.search]);

  return (
    <Box>
      {suggestedResponses.length > 0 && !suggestedResponseUsed && (
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            padding: '16px 36px 16px 12px',
            alignItems: 'flex-start',
            alignSelf: 'stretch',
            borderTop: '0.5px solid #C1C9D0',
            background: '#FFF',
          }}
        >
          <IconButton
            size="small"
            aria-label="Dismiss suggested responses"
            onClick={() => setSuggestedResponseUsed(true)}
            sx={{ position: 'absolute', top: 4, right: 4, px: 1, color: 'text.secondary' }}
          >
            <Iconify icon="eva:close-fill" width={16} />
          </IconButton>
          <Stack
            direction="row"
            spacing={0.75}
            flexWrap="wrap"
            useFlexGap
            sx={{
              rowGap: '6px',
              width: '100%',
            }}
          >
            {suggestedResponses.map((response, index) => (
              <Chip
                key={index}
                label={response.text}
                size="small"
                variant="outlined"
                onClick={() => {
                  setPrependText({ text: response.text, key: Date.now() });
                  setSuggestedResponseUsed(true);
                }}
                sx={{
                  color: '#212B36',
                  border: '1px solid #DFE3E8',
                  borderRadius: '10px',
                  height: 'auto',

                  fontFamily: 'Public Sans, sans-serif',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: 'normal',

                  '& .MuiChip-label': {
                    padding: '8px',
                  },
                }}
              />
            ))}
          </Stack>
        </Box>
      )}
      {users && users?.length > 0 ? (
        <>
          <Box sx={{ display: 'flex', flexGrow: 1 }}>
            <Stack direction="column" alignItems="flex-end" sx={{ flexGrow: 1 }}>
              <Editor
                attachFile
                printChat
                simple
                id="ticket-reply"
                value={message}
                aiHelp
                onChange={(value) => setMessage(value)}
                prependText={prependText}
                placeholder="Type a message"
                sx={{ flexGrow: 1, borderColor: 'transparent' }}
                users={users}
                handleFileInputChange={handleFileInputChange}
                emailSwitch
                sendButton
                handleSend={handleSend}
                ticketEmailsOn={ticketEmailsOn}
                setTicketEmailsOn={setTicketEmailsOn}
                displayedTicket={subticketId || ticketNumber || id}
                currentTicket={parseInt(id, 10)}
                attachments={attachments}
                removeFile={removeFile}
              />
            </Stack>
          </Box>
        </>
      ) : (
        //no users input
        <Editor
          attachFile
          printChat
          simple
          id="ticket-reply"
          value={message}
          onChange={(value) => setMessage(value)}
          prependText={prependText}
          placeholder="Type a message"
          sx={{ flexGrow: 1, borderColor: 'transparent' }}
          handleFileInputChange={handleFileInputChange}
          emailSwitch
          sendButton
          handleSend={handleSend}
          ticketEmailsOn={ticketEmailsOn}
          setTicketEmailsOn={setTicketEmailsOn}
          displayedTicket={subticketId || ticketNumber || id}
          currentTicket={parseInt(id, 10)}
        />
      )}
    </Box>
  );
}
